
import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  ArrowPathIcon,
  BellAlertIcon,
  EyeIcon,
  MagnifyingGlassIcon,
  PaperClipIcon,
  TrashIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import { toast } from "react-toastify";
import ApiCustom from "../../utils/ApiCustom";
import TextEditor from "../TextEditor";

const TEMPLATE_VARIABLES = [
  {
    label: "Tên đơn vị",
    token: "{{companyName}}",
  },
  {
    label: "Người đại diện",
    token: "{{representativeName}}",
  },
  {
    label: "Tên kỳ phí",
    token: "{{membershipFeeTitle}}",
  },
  {
    label: "Số tiền",
    token: "{{amount}}",
  },
  {
    label: "Ngày bắt đầu",
    token: "{{payStartDay}}",
  },
];

const DEFAULT_CONTENT =
  "<p>Kính gửi: Quý Lãnh đạo {{companyName}},</p>" +
  "<p>Hiệp hội trân trọng thông báo đến Quý hội viên về việc thu {{membershipFeeTitle}}.</p>" +
  "<p>Số tiền cần đóng: <strong>{{amount}}</strong>.</p>" +
  "<p>Ngày bắt đầu đóng: <strong>{{payStartDay}}</strong>.</p>" +
  "<p>Kính mong Quý hội viên quan tâm và thực hiện đúng thời gian.</p>" +
  "<p>Trân trọng.</p>";

const RemindMembershipFeeModal = ({ membershipFee, onClose }) => {
  const subjectInputRef = useRef(null);
  const editorRef = useRef(null);
  const fileInputRef = useRef(null);
  const subjectSelectionRef = useRef({
    start: 0,
    end: 0,
  });

  const [reminderData, setReminderData] = useState({
    membershipFee: membershipFee || null,
    recipients: [],
  });
  const [selectedCompanyIds, setSelectedCompanyIds] = useState([]);
  const [searchValue, setSearchValue] = useState("");
  const [subject, setSubject] = useState(
    "Thông báo về việc thu hội phí hội viên"
  );
  const [content, setContent] = useState(DEFAULT_CONTENT);
  const [attachments, setAttachments] = useState([]);
  const [activeInsertTarget, setActiveInsertTarget] =
    useState("content");
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);

  const membershipFeeId = membershipFee?.id;

  useEffect(() => {
    if (!membershipFeeId) {
      return;
    }

    const fetchRecipients = async () => {
      setLoading(true);

      try {
        const response = await ApiCustom.get(
          `/membership-fee-reminder-recipients/${membershipFeeId}`
        );
        const payload = response.data?.data?.recipients
          ? response.data.data
          : response.data?.data?.data || {
              membershipFee,
              recipients: [],
            };

        const recipients = payload.recipients || [];
        const receivableCompanyIds = recipients
          .filter((recipient) => recipient.canReceive)
          .map((recipient) => recipient.companyId);

        setReminderData({
          membershipFee: payload.membershipFee || membershipFee,
          recipients,
        });
        setSelectedCompanyIds(receivableCompanyIds);
      } catch (error) {
        toast.error(
          error.response?.data?.messages ||
            "Không thể lấy danh sách hội viên chưa đóng hội phí!"
        );
      } finally {
        setLoading(false);
      }
    };

    fetchRecipients();
  }, [membershipFeeId]);

  const filteredRecipients = useMemo(() => {
    const keyword = searchValue.trim().toLowerCase();

    if (!keyword) {
      return reminderData.recipients;
    }

    return reminderData.recipients.filter((recipient) =>
      [
        recipient.companyName,
        recipient.representativeName,
        recipient.email,
      ]
        .filter(Boolean)
        .some((value) =>
          String(value).toLowerCase().includes(keyword)
        )
    );
  }, [reminderData.recipients, searchValue]);

  const selectableFilteredRecipients = filteredRecipients.filter(
    (recipient) => recipient.canReceive
  );

  const totalReceivableRecipients = reminderData.recipients.filter(
    (recipient) => recipient.canReceive
  ).length;

  const areAllFilteredSelected =
    selectableFilteredRecipients.length > 0 &&
    selectableFilteredRecipients.every((recipient) =>
      selectedCompanyIds.includes(recipient.companyId)
    );

  const handleToggleRecipient = (companyId) => {
    setSelectedCompanyIds((currentIds) =>
      currentIds.includes(companyId)
        ? currentIds.filter((id) => id !== companyId)
        : [...currentIds, companyId]
    );
  };

  const handleToggleAllFiltered = () => {
    const filteredCompanyIds = selectableFilteredRecipients.map(
      (recipient) => recipient.companyId
    );

    if (areAllFilteredSelected) {
      setSelectedCompanyIds((currentIds) =>
        currentIds.filter(
          (id) => !filteredCompanyIds.includes(id)
        )
      );
      return;
    }

    setSelectedCompanyIds((currentIds) => [
      ...new Set([...currentIds, ...filteredCompanyIds]),
    ]);
  };

  const rememberSubjectSelection = () => {
    const input = subjectInputRef.current;

    if (!input) {
      return;
    }

    subjectSelectionRef.current = {
      start: input.selectionStart ?? subject.length,
      end: input.selectionEnd ?? subject.length,
    };
  };

  const handleInsertVariable = (token) => {
    if (activeInsertTarget === "subject") {
      const input = subjectInputRef.current;
      const start =
        subjectSelectionRef.current.start ?? subject.length;
      const end =
        subjectSelectionRef.current.end ?? subject.length;
      const nextSubject =
        subject.slice(0, start) + token + subject.slice(end);
      const nextPosition = start + token.length;

      setSubject(nextSubject);

      requestAnimationFrame(() => {
        input?.focus();
        input?.setSelectionRange(nextPosition, nextPosition);
        subjectSelectionRef.current = {
          start: nextPosition,
          end: nextPosition,
        };
      });

      return;
    }

    editorRef.current?.insertHTML(token);
    setActiveInsertTarget("content");
  };

  const handleAttachmentChange = (event) => {
    const selectedFiles = Array.from(event.target.files || []);

    setAttachments((currentFiles) => {
      const nextFiles = [...currentFiles];

      selectedFiles.forEach((file) => {
        const isDuplicate = nextFiles.some(
          (currentFile) =>
            currentFile.name === file.name &&
            currentFile.size === file.size &&
            currentFile.lastModified === file.lastModified
        );

        if (!isDuplicate && nextFiles.length < 5) {
          nextFiles.push(file);
        }
      });

      if (currentFiles.length + selectedFiles.length > 5) {
        toast.warning("Chỉ được đính kèm tối đa 5 file.");
      }

      return nextFiles;
    });

    event.target.value = "";
  };

  const handleRemoveAttachment = (index) => {
    setAttachments((currentFiles) =>
      currentFiles.filter(
        (_, fileIndex) => fileIndex !== index
      )
    );
  };

  const replacePreviewVariables = (value, recipient) => {
    const currentMembershipFee =
      reminderData.membershipFee || membershipFee || {};
    const replacements = {
      "{{companyName}}":
        recipient?.companyName || "Tên đơn vị",
      "{{representativeName}}":
        recipient?.representativeName || "Người đại diện",
      "{{membershipFeeTitle}}":
        currentMembershipFee.title || "Tên kỳ phí",
      "{{amount}}": Number(
        currentMembershipFee.fee || 0
      ).toLocaleString("vi-VN", {
        style: "currency",
        currency: "VND",
      }),
      "{{payStartDay}}": currentMembershipFee.payStartDay
        ? new Date(
            currentMembershipFee.payStartDay
          ).toLocaleDateString("vi-VN")
        : "",
    };

    return Object.entries(replacements).reduce(
      (result, [token, replacement]) =>
        result.split(token).join(replacement),
      String(value || "")
    );
  };

  const previewRecipient =
    reminderData.recipients.find((recipient) =>
      selectedCompanyIds.includes(recipient.companyId)
    ) || reminderData.recipients[0];

  const previewSubject = replacePreviewVariables(
    subject,
    previewRecipient
  );
  const previewContent = replacePreviewVariables(
    content,
    previewRecipient
  );

  const handleSend = async () => {
    if (!subject.trim()) {
      toast.error("Vui lòng nhập tiêu đề email.");
      return;
    }

    if (!content.trim() || content === "<p><br></p>") {
      toast.error("Vui lòng nhập nội dung email.");
      return;
    }

    if (selectedCompanyIds.length === 0) {
      toast.error(
        "Vui lòng chọn ít nhất một hội viên để gửi."
      );
      return;
    }

    const formData = new FormData();
    formData.append("subject", subject.trim());
    formData.append("content", content);
    formData.append(
      "companyIds",
      JSON.stringify(selectedCompanyIds)
    );

    attachments.forEach((file) => {
      formData.append("files", file);
    });

    setSending(true);

    try {
      const response = await ApiCustom.post(
        `/membership-fee-send-reminder/${membershipFeeId}`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
          timeout: 300000,
        }
      );

      const result = response.data?.data || {};
      toast.success(
        `Đã gửi thành công ${
          result.totalSent || 0
        } email nhắc hội phí.`
      );

      if (
        (result.totalFailed || 0) > 0 ||
        (result.totalSkipped || 0) > 0
      ) {
        toast.warning(
          `Có ${
            result.totalFailed || 0
          } email gửi lỗi và ${
            result.totalSkipped || 0
          } hội viên chưa có email.`
        );
      }

      onClose();
    } catch (error) {
      toast.error(
        error.response?.data?.messages ||
          "Đã xảy ra lỗi khi gửi email nhắc hội phí!"
      );
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 p-3">
      <div className="flex h-[94vh] w-[98vw] flex-col overflow-hidden rounded-2xl bg-base-100 shadow-2xl">
        <div className="flex items-start justify-between border-b px-6 py-4">
          <div>
            <h2 className="text-2xl font-bold">
              Soạn email nhắc hội phí
            </h2>
            <p className="mt-1 text-sm text-gray-500">
              Soạn nội dung, kiểm tra người nhận rồi gửi email
              nhắc hội phí.
            </p>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="btn btn-ghost btn-sm"
            title="Đóng"
            disabled={sending}
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        <div className="grid min-h-0 flex-1 grid-cols-1 gap-4 overflow-y-auto px-6 py-5 xl:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
          <div className="min-h-0 overflow-y-auto pr-1 xl:pr-4">
            <div className="rounded-2xl border bg-base-100 p-4">
              <label className="mb-2 block text-sm font-semibold">
                Tiêu đề email
              </label>
              <input
                ref={subjectInputRef}
                type="text"
                value={subject}
                onChange={(event) =>
                  setSubject(event.target.value)
                }
                onFocus={() => {
                  setActiveInsertTarget("subject");
                  rememberSubjectSelection();
                }}
                onClick={rememberSubjectSelection}
                onKeyUp={rememberSubjectSelection}
                onSelect={rememberSubjectSelection}
                className="input input-bordered w-full"
                placeholder="Nhập tiêu đề email"
              />

              <div className="mt-4 rounded-2xl border border-sky-200 bg-sky-50 p-4">
                <p className="font-semibold text-sky-800">
                  Chèn nhanh thông tin hội viên
                </p>
                <div className="mt-3 flex flex-wrap gap-2">
                  {TEMPLATE_VARIABLES.map((variable) => (
                    <button
                      key={variable.token}
                      type="button"
                      onMouseDown={(event) =>
                        event.preventDefault()
                      }
                      onClick={() =>
                        handleInsertVariable(variable.token)
                      }
                      className="rounded-full border border-sky-300 bg-white px-4 py-2 text-sm text-sky-700 hover:bg-sky-100"
                    >
                      {variable.label}
                    </button>
                  ))}
                </div>
                <p className="mt-3 text-xs text-sky-600">
                  Đặt con trỏ vào tiêu đề hoặc nội dung email
                  rồi bấm biến cần chèn.
                </p>
              </div>
            </div>

            <div className="mt-4 rounded-2xl border border-dashed border-gray-300 bg-base-100 p-4">
              <div className="flex items-center justify-between gap-4">
                <div className="flex min-w-0 items-center gap-3">
                  <div className="rounded-xl border bg-white p-2">
                    <PaperClipIcon className="h-6 w-6 text-gray-600" />
                  </div>
                  <div className="min-w-0">
                    <p className="font-semibold">
                      Đính kèm công văn/PDF thu hội phí
                    </p>
                    <p className="text-sm text-gray-500">
                      Có thể đính kèm PDF, ảnh, Word hoặc Excel
                      để gửi cùng email.
                    </p>
                  </div>
                </div>

                <button
                  type="button"
                  onClick={() =>
                    fileInputRef.current?.click()
                  }
                  className="btn btn-outline btn-sm whitespace-nowrap"
                >
                  Chọn file
                </button>
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  onChange={handleAttachmentChange}
                  className="hidden"
                />
              </div>

              {attachments.length > 0 && (
                <div className="mt-3 space-y-2 border-t pt-3">
                  {attachments.map((file, index) => (
                    <div
                      key={`${file.name}-${file.lastModified}`}
                      className="flex items-center justify-between gap-3 rounded-lg bg-base-200 px-3 py-2 text-sm"
                    >
                      <div className="min-w-0">
                        <p className="truncate font-medium">
                          {file.name}
                        </p>
                        <p className="text-xs text-gray-500">
                          {(file.size / 1024).toFixed(1)} KB
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={() =>
                          handleRemoveAttachment(index)
                        }
                        className="btn btn-ghost btn-xs text-red-600"
                        title="Bỏ file"
                      >
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="mt-4 rounded-2xl border bg-base-100 p-4">
              <label className="mb-2 block text-sm font-semibold">
                Nội dung email
              </label>
              <TextEditor
                ref={editorRef}
                name="content"
                value={content}
                onFocus={() =>
                  setActiveInsertTarget("content")
                }
                onChange={({ value }) => setContent(value)}
                canUpdate
                height="310px"
              />
            </div>
          </div>

          <div className="flex min-h-[540px] flex-col overflow-hidden rounded-2xl border bg-base-100">
            <div className="flex flex-col gap-3 border-b px-4 py-4 lg:flex-row lg:items-center lg:justify-between">
              <div>
                <h3 className="font-semibold">
                  Danh sách người nhận
                </h3>
                <p className="text-sm text-gray-500">
                  Đã chọn{" "}
                  <span className="font-semibold text-blue-600">
                    {selectedCompanyIds.length}
                  </span>{" "}
                  / {totalReceivableRecipients} hội viên
                </p>
              </div>

              <div className="relative w-full lg:max-w-sm">
                <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
                <input
                  type="search"
                  value={searchValue}
                  onChange={(event) =>
                    setSearchValue(event.target.value)
                  }
                  placeholder="Tìm đơn vị, người đại diện hoặc email..."
                  className="input input-bordered input-sm w-full pl-10"
                />
              </div>
            </div>

            {loading ? (
              <div className="flex flex-1 items-center justify-center">
                <ArrowPathIcon className="h-8 w-8 animate-spin text-blue-500" />
              </div>
            ) : (
              <div className="min-h-0 flex-1 overflow-auto">
                <table className="table table-pin-rows table-sm">
                  <thead>
                    <tr>
                      <th className="w-12">
                        <input
                          type="checkbox"
                          className="checkbox checkbox-sm"
                          checked={areAllFilteredSelected}
                          onChange={handleToggleAllFiltered}
                          disabled={
                            selectableFilteredRecipients.length ===
                            0
                          }
                        />
                      </th>
                      <th>Tên đơn vị</th>
                      <th>Người đại diện</th>
                      <th>Email nhận thông báo</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredRecipients.length > 0 ? (
                      filteredRecipients.map((recipient) => (
                        <tr
                          key={recipient.companyId}
                          className={
                            recipient.canReceive
                              ? "hover:bg-base-200"
                              : "bg-red-50 opacity-70"
                          }
                        >
                          <td>
                            <input
                              type="checkbox"
                              className="checkbox checkbox-sm"
                              checked={selectedCompanyIds.includes(
                                recipient.companyId
                              )}
                              onChange={() =>
                                handleToggleRecipient(
                                  recipient.companyId
                                )
                              }
                              disabled={!recipient.canReceive}
                            />
                          </td>
                          <td className="min-w-72 whitespace-normal">
                            {recipient.companyName || "-"}
                          </td>
                          <td className="min-w-36 whitespace-normal">
                            {recipient.representativeName ||
                              "Chưa có người đại diện"}
                          </td>
                          <td className="min-w-56 break-all">
                            {recipient.email || (
                              <span className="text-xs text-red-600">
                                Chưa có email
                              </span>
                            )}
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td
                          colSpan={4}
                          className="py-8 text-center text-gray-500"
                        >
                          Không tìm thấy hội viên phù hợp.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        <div className="flex flex-col-reverse gap-2 border-t bg-base-100 px-6 py-4 sm:flex-row sm:justify-end">
          <button
            type="button"
            onClick={onClose}
            className="btn btn-outline"
            disabled={sending}
          >
            Đóng
          </button>

          <button
            type="button"
            onClick={() => setIsPreviewOpen(true)}
            className="btn btn-outline btn-info"
            disabled={!subject.trim() || !content.trim()}
          >
            <EyeIcon className="h-5 w-5" />
            Xem trước
          </button>

          <button
            type="button"
            onClick={handleSend}
            className="btn btn-primary"
            disabled={
              loading ||
              sending ||
              selectedCompanyIds.length === 0
            }
          >
            {sending ? (
              <ArrowPathIcon className="h-5 w-5 animate-spin" />
            ) : (
              <BellAlertIcon className="h-5 w-5" />
            )}
            {sending ? "Đang gửi..." : "Gửi thông báo"}
          </button>
        </div>
      </div>

      {isPreviewOpen && (
        <div className="fixed inset-0 z-[120] flex items-center justify-center bg-black/60 p-4">
          <div className="flex max-h-[90vh] w-full max-w-4xl flex-col overflow-hidden rounded-2xl bg-base-100 shadow-2xl">
            <div className="flex items-start justify-between border-b px-6 py-4">
              <div>
                <h3 className="text-xl font-bold">
                  Xem trước email
                </h3>
                <p className="mt-1 text-sm text-gray-500">
                  Dữ liệu mẫu được lấy từ hội viên đầu tiên đang
                  được chọn.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setIsPreviewOpen(false)}
                className="btn btn-ghost btn-sm"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            <div className="overflow-y-auto p-6">
              <div className="rounded-xl border bg-base-200 p-4">
                <p className="text-xs font-semibold uppercase text-gray-500">
                  Tiêu đề
                </p>
                <p className="mt-1 text-lg font-semibold">
                  {previewSubject}
                </p>
              </div>

              <div
                className="prose mt-4 max-w-none rounded-xl border bg-white p-6"
                dangerouslySetInnerHTML={{
                  __html: previewContent,
                }}
              />
            </div>

            <div className="flex justify-end border-t px-6 py-4">
              <button
                type="button"
                onClick={() => setIsPreviewOpen(false)}
                className="btn btn-primary"
              >
                Đóng xem trước
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RemindMembershipFeeModal;

